import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  FileText,
  Loader2,
  Link,
  Unlink,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  ChevronDown,
  ChevronUp,
  Building2
} from "lucide-react";

const formatCurrency = (value) => {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString('de-DE');
};

export function LexwareSettings({ toast }) {
  const [lexwareConnected, setLexwareConnected] = useState(false);
  const [lexwareConnecting, setLexwareConnecting] = useState(false);
  const [lexwareSyncing, setLexwareSyncing] = useState(false);
  const [lexwareApiKey, setLexwareApiKey] = useState("");
  const [lexwareYear, setLexwareYear] = useState(2025);
  const [lexwareData, setLexwareData] = useState(null);
  const [lexwareYearlyTotals, setLexwareYearlyTotals] = useState(null);
  const [expandedMonths, setExpandedMonths] = useState({});

  const fetchLexwareStatus = useCallback(async () => {
    try {
      const response = await api.get("/lexware/status");
      setLexwareConnected(response.data.connected);
    } catch (error) {
      setLexwareConnected(false);
    }
  }, []);

  const fetchLexwareData = useCallback(async () => {
    if (!lexwareConnected) return;
    setLexwareSyncing(true);
    try {
      const response = await api.get("/lexware/invoices/monthly/" + lexwareYear);
      if (response.data.success) {
        setLexwareData(response.data.months);
        setLexwareYearlyTotals(response.data.yearly_totals);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Laden der Lexware-Daten");
    } finally {
      setLexwareSyncing(false);
    }
  }, [lexwareConnected, lexwareYear, toast]);

  useEffect(() => {
    fetchLexwareStatus();
  }, [fetchLexwareStatus]);

  useEffect(() => {
    if (lexwareConnected) {
      fetchLexwareData();
    }
  }, [lexwareConnected, lexwareYear, fetchLexwareData]);

  const handleLexwareConnect = async () => {
    if (!lexwareApiKey.trim()) {
      toast.error("Bitte API-Key eingeben");
      return;
    }
    setLexwareConnecting(true);
    try {
      const response = await api.post("/lexware/connect", { api_key: lexwareApiKey });
      if (response.data.success) {
        toast.success("Lexware verbunden");
        setLexwareConnected(true);
        setLexwareApiKey("");
        fetchLexwareData();
      } else {
        toast.error(response.data.message || "Verbindung fehlgeschlagen");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Lexware-Verbindung fehlgeschlagen");
    } finally {
      setLexwareConnecting(false);
    }
  };

  const handleLexwareDisconnect = async () => {
    try {
      await api.post("/lexware/disconnect");
      toast.success("Lexware getrennt");
      setLexwareConnected(false);
      setLexwareData(null);
      setLexwareYearlyTotals(null);
    } catch (error) {
      toast.error("Fehler beim Trennen");
    }
  };

  const toggleMonth = (monthNum) => {
    setExpandedMonths(prev => ({ ...prev, [monthNum]: !prev[monthNum] }));
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <CardTitle>Lexware Office</CardTitle>
              <CardDescription>
                Eingangs- und Ausgangsrechnungen aus Lexware Office
              </CardDescription>
            </div>
          </div>
          <Badge variant={lexwareConnected ? "default" : "secondary"}>
            {lexwareConnected ? "Verbunden" : "Nicht verbunden"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {!lexwareConnected ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="lexware_api_key">Lexware API-Key</Label>
              <Input
                id="lexware_api_key"
                type="password"
                value={lexwareApiKey}
                onChange={(e) => setLexwareApiKey(e.target.value)}
                placeholder="Ihr Lexware API-Key"
                data-testid="lexware-api-key-input"
              />
              <p className="text-xs text-muted-foreground">
                Den API-Key finden Sie unter: Lexware Office → Einstellungen → Add-ons → Public API
              </p>
            </div>
            <Button onClick={handleLexwareConnect} disabled={lexwareConnecting} data-testid="connect-lexware-btn">
              {lexwareConnecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link className="w-4 h-4 mr-2" />}
              {lexwareConnecting ? "Verbinden..." : "Verbinden"}
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <select
                  className="border rounded px-3 py-2"
                  value={lexwareYear}
                  onChange={(e) => setLexwareYear(Number(e.target.value))}
                  data-testid="lexware-year-select"
                >
                  <option value={2025}>2025</option>
                  <option value={2026}>2026</option>
                </select>
                <Button variant="outline" size="sm" onClick={fetchLexwareData} disabled={lexwareSyncing}>
                  <RefreshCw className={lexwareSyncing ? "w-4 h-4 mr-2 animate-spin" : "w-4 h-4 mr-2"} />
                  Aktualisieren
                </Button>
              </div>
              <Button variant="outline" onClick={handleLexwareDisconnect}>
                <Unlink className="w-4 h-4 mr-2" />
                Trennen
              </Button>
            </div>

            {lexwareYearlyTotals && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-sm text-muted-foreground">Ausgangsrechnungen</div>
                    <div className="text-xl font-bold text-green-600">{formatCurrency(lexwareYearlyTotals.outgoing_total)}</div>
                    <div className="text-xs text-muted-foreground">{lexwareYearlyTotals.outgoing_count} Rechnungen</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-sm text-muted-foreground">Eingangsrechnungen</div>
                    <div className="text-xl font-bold text-red-600">{formatCurrency(lexwareYearlyTotals.incoming_total)}</div>
                    <div className="text-xs text-muted-foreground">{lexwareYearlyTotals.incoming_count} Rechnungen</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-sm text-muted-foreground">Saldo</div>
                    <div className={"text-xl font-bold " + (lexwareYearlyTotals.balance >= 0 ? "text-green-600" : "text-red-600")}>{formatCurrency(lexwareYearlyTotals.balance)}</div>
                    <div className="text-xs text-muted-foreground">Einnahmen - Ausgaben</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-sm text-muted-foreground">Gesamt</div>
                    <div className="text-xl font-bold">{lexwareYearlyTotals.incoming_count + lexwareYearlyTotals.outgoing_count}</div>
                    <div className="text-xs text-muted-foreground">Rechnungen in {lexwareYear}</div>
                  </CardContent>
                </Card>
              </div>
            )}

            {lexwareData && (
              <div className="space-y-2">
                <h3 className="font-semibold">Monatliche Übersicht</h3>
                {Object.entries(lexwareData).map(([monthNum, data]) => (
                  <div key={monthNum} className="border rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleMonth(monthNum)}
                      className="flex flex-col sm:flex-row sm:items-center justify-between w-full gap-1 p-3 bg-muted/50 hover:bg-muted transition-colors text-left"
                    >
                      <span className="font-medium">{data.month_name}</span>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                        <span className="text-green-600">
                          <TrendingUp className="w-4 h-4 inline mr-1" />
                          {formatCurrency(data.outgoing_total)} ({data.outgoing_count})
                        </span>
                        <span className="text-red-600">
                          <TrendingDown className="w-4 h-4 inline mr-1" />
                          {formatCurrency(data.incoming_total)} ({data.incoming_count})
                        </span>
                        <span className={data.balance >= 0 ? "text-green-600 font-medium" : "text-red-600 font-medium"}>
                          = {formatCurrency(data.balance)}
                        </span>
                        {expandedMonths[monthNum] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </div>
                    </button>
                    {expandedMonths[monthNum] && data.invoices.length > 0 && (
                      <div className="border-t">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Datum</TableHead>
                              <TableHead>Nummer</TableHead>
                              <TableHead>Typ</TableHead>
                              <TableHead>Kontakt</TableHead>
                              <TableHead className="text-right">Betrag</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {data.invoices.map((invoice, idx) => (
                              <TableRow key={invoice.id || idx}>
                                <TableCell>{formatDate(invoice.voucher_date)}</TableCell>
                                <TableCell className="font-mono text-sm">{invoice.voucher_number || "-"}</TableCell>
                                <TableCell>
                                  <Badge variant="outline" className={invoice.type === "incoming" ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"}>
                                    {invoice.type_label}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Building2 className="w-3 h-3 inline mr-1 text-muted-foreground" />
                                  {invoice.contact_name || "-"}
                                </TableCell>
                                <TableCell className={"text-right font-medium " + (invoice.type === "incoming" ? "text-red-600" : "text-green-600")}>
                                  {invoice.type === "incoming" ? "-" : "+"}{formatCurrency(invoice.total_amount)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                    {expandedMonths[monthNum] && data.invoices.length === 0 && (
                      <p className="text-muted-foreground text-center py-4">Keine Rechnungen in diesem Monat</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
